import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/lib/auth'
import { hasSuperAdminAccess } from '@/lib/permissions'
import {
  type SystemFeatureGroup,
  type SystemFeatureGroupId,
  type SystemFeatureKey,
  type SystemFeatureVisibility,
  getSystemFeatureGroupSummary,
  readSystemFeatureVisibility,
  resetSystemFeatureGroupVisibility,
  resetSystemFeatureVisibility,
  setSystemFeatureVisible,
  systemFeatureGroups,
} from '@/lib/system-feature-visibility'
import { cn } from '@/lib/utils'
import { useLocation } from '@tanstack/react-router'
import {
  Building2,
  Eye,
  EyeOff,
  FileText,
  Lock,
  RotateCcw,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

function getGroupIdFromPath(pathname: string): SystemFeatureGroupId {
  const segment = pathname.split('/').filter(Boolean).at(-1)
  if (segment === 'navigation') return segment
  if (segment === 'forms' || segment === 'customers' || segment === 'sales' || segment === 'runtime')
    return segment
  if (segment === 'share') return segment
  return 'navigation'
}

const mainFeatureIcons: Record<SystemFeatureGroupId, LucideIcon> = {
  navigation: SlidersHorizontal,
  share: Share2,
  forms: FileText,
  customers: Building2,
  sales: Users,
  runtime: Settings,
}

export default function SystemRoute() {
  const location = useLocation()
  const { user } = useAuth()
  const [visibility, setVisibility] = useState<SystemFeatureVisibility>(() =>
    readSystemFeatureVisibility(),
  )
  const [featureSearch, setFeatureSearch] = useState('')
  const activeGroupId = getGroupIdFromPath(location.pathname)
  const activeGroup = useMemo(
    () => systemFeatureGroups.find((group) => group.id === activeGroupId) ?? systemFeatureGroups[0],
    [activeGroupId],
  )
  const activeGroupSummary = useMemo(
    () => getSystemFeatureGroupSummary(activeGroup.id, visibility),
    [activeGroup.id, visibility],
  )
  const filteredFeatures = useMemo(() => {
    const normalizedSearch = featureSearch.trim().toLocaleLowerCase()
    if (!normalizedSearch) return activeGroup.features

    return activeGroup.features.filter((feature) =>
      [feature.label, feature.description, feature.key].some((value) =>
        value.toLocaleLowerCase().includes(normalizedSearch),
      ),
    )
  }, [activeGroup.features, featureSearch])

  if (!hasSuperAdminAccess(user)) {
    return (
      <div className="grid min-h-full place-items-center p-6">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="grid size-10 place-items-center rounded-lg bg-slate-100 text-slate-500">
                <Lock className="size-5" />
              </div>
              <div>
                <CardTitle>需要超級管理員權限</CardTitle>
                <p className="mt-1 text-sm text-slate-500">
                  系統功能顯示設定只開放給 preview 超級管理員，不連接真實後端權限。
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    )
  }

  function handleToggle(key: SystemFeatureKey, checked: boolean) {
    const next = setSystemFeatureVisible(key, checked)
    setVisibility(next)
  }

  function handleResetGroup() {
    const next = resetSystemFeatureGroupVisibility(activeGroup.id)
    setVisibility(next)
  }

  function handleResetAll() {
    const next = resetSystemFeatureVisibility()
    setVisibility(next)
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-5 p-4 lg:p-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-950">系統功能顯示設定</h1>
            {visibility['runtime.statusBadges'] ? (
              <>
                <Badge variant="outline">frontend-only</Badge>
                <Badge variant="outline">mock-safe</Badge>
              </>
            ) : null}
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
            控制主功能內的子功能與區塊是否顯示。這裡只保存瀏覽器本機設定，不啟用
            Supabase、R2、OAuth、Email、Webhook、analytics、AI 或外部服務。
          </p>
        </div>
      </div>

      <div className="grid min-h-0 gap-5 xl:grid-cols-[260px_minmax(0,1fr)]">
        <Card className="min-h-0">
          <CardHeader>
            <CardTitle className="text-base">系統</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2">
            <button
              type="button"
              aria-current="page"
              className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-lg border border-[#4a2f21]/20 bg-[#4a2f21]/5 px-3 py-3 text-left text-[#4a2f21]"
            >
              <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-md bg-white text-[#4a2f21]">
                <SlidersHorizontal className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold">系統功能顯示設定</span>
                <span className="mt-1 block text-xs text-[#4a2f21]/75">
                  管理各功能區塊是否顯示。
                </span>
              </span>
            </button>
          </CardContent>
        </Card>

        <Card className="min-h-0">
          <CardHeader className="border-b border-slate-200">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <CardTitle>{activeGroup.label}</CardTitle>
                <p className="mt-1 text-sm text-slate-500">{activeGroup.description}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge variant="secondary">
                    顯示 {activeGroupSummary.visibleCount} / {activeGroupSummary.totalCount}
                  </Badge>
                  <Badge variant="outline">隱藏 {activeGroupSummary.hiddenCount} 項</Badge>
                  <Badge variant="outline">已調整 {activeGroupSummary.changedCount} 項</Badge>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {visibility['runtime.statusBadges'] ? (
                  <Badge variant="outline" className="w-fit bg-slate-50">
                    local visibility
                  </Badge>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label="重設此主功能預設"
                  title={`重設${activeGroup.label}預設`}
                  onClick={handleResetGroup}
                >
                  <RotateCcw className="size-3.5" />
                  重設此主功能
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label="全部重設為預設"
                  onClick={handleResetAll}
                >
                  <RotateCcw className="size-3.5" />
                  全部重設
                </Button>
              </div>
            </div>
            <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {systemFeatureGroups.map((group) => (
                <MainFeatureButton
                  key={group.id}
                  group={group}
                  active={group.id === activeGroup.id}
                />
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {activeGroup.features.length ? (
              <>
                <div className="border-b border-slate-200 p-4">
                  <div className="relative max-w-xl">
                    <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 size-4 text-slate-400" />
                    <Input
                      type="search"
                      aria-label="搜尋功能設定"
                      placeholder="搜尋功能名稱、描述或 key"
                      value={featureSearch}
                      className="pl-9"
                      onChange={(event) => setFeatureSearch(event.target.value)}
                    />
                  </div>
                </div>
                {filteredFeatures.length ? (
                  <div className="divide-y divide-slate-200">
                    {filteredFeatures.map((feature) => {
                      const checked = visibility[feature.key]
                      return (
                        <div
                          key={feature.key}
                          className="grid gap-3 p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center"
                        >
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              {checked ? (
                                <Eye className="size-4 text-emerald-600" />
                              ) : (
                                <EyeOff className="size-4 text-slate-400" />
                              )}
                              <div className="font-semibold text-slate-950">{feature.label}</div>
                              <Badge variant={checked ? 'success' : 'secondary'}>
                                {checked ? '顯示' : '隱藏'}
                              </Badge>
                            </div>
                            <p className="mt-1 text-sm leading-6 text-slate-500">
                              {feature.description}
                            </p>
                            <p className="mt-1 font-mono text-xs text-slate-400">{feature.key}</p>
                          </div>
                          <Switch
                            aria-label={`${feature.label} 顯示`}
                            checked={checked}
                            onCheckedChange={(nextChecked) =>
                              handleToggle(feature.key, nextChecked)
                            }
                          />
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="grid min-h-[220px] place-items-center p-6 text-center">
                    <div className="max-w-md">
                      <div className="mx-auto grid size-12 place-items-center rounded-lg bg-slate-100 text-slate-500">
                        <Search className="size-5" />
                      </div>
                      <h2 className="mt-4 font-semibold text-slate-950">找不到符合的功能設定</h2>
                      <p className="mt-2 text-sm leading-6 text-slate-500">
                        請改用功能名稱、描述或 registry key 搜尋；這裡只篩選目前主功能。
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="grid min-h-[280px] place-items-center p-6 text-center">
                <div className="max-w-md">
                  <div className="mx-auto grid size-12 place-items-center rounded-lg bg-slate-100 text-slate-500">
                    <ShieldCheck className="size-5" />
                  </div>
                  <h2 className="mt-4 font-semibold text-slate-950">尚未接入顯示設定</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    這個主功能目前只保留系統設定入口。後續若要控制其子功能顯示，需先補 frontend-only
                    計劃、測試與對應 UI 接線。
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function MainFeatureButton({ group, active }: { group: SystemFeatureGroup; active: boolean }) {
  const Icon = mainFeatureIcons[group.id]

  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      className={cn(
        'grid grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
        active
          ? 'border-[#4a2f21]/20 bg-[#4a2f21]/5 text-[#4a2f21]'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
      )}
      onClick={() => {
        window.history.pushState(null, '', `/dashboard/system/${group.id}`)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }}
    >
      <span
        className={cn(
          'mt-0.5 grid size-8 shrink-0 place-items-center rounded-md',
          active ? 'bg-white text-[#4a2f21]' : 'bg-slate-100 text-slate-500',
        )}
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{group.label}</span>
        <span className={cn('mt-1 block text-xs', active ? 'text-[#4a2f21]/75' : 'text-slate-500')}>
          {group.description}
        </span>
      </span>
    </button>
  )
}
